import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Input,
  Stack,
} from "@mui/joy";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import CustomModal from "components/common/CustomModal";
import LinkV2 from "components/common/LinkV2";
import { PATH } from "routes/paths";
import {
  ambassadorApi,
  type AmbassadorContractAgreementSection,
  type AmbassadorContractType,
  getAmbassadorToken,
} from "api/ambassadorApi";
import { formatPhoneNumberInput } from "utils/phoneNumberUtils";
import { CONTRACT_FILE_MAX_BYTES, readFileAsDataUrl } from "utils/fileUtils";
import distancingLogo from "static/image/common/distancing_logo.png";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

const CONTRACT_TYPE_LABELS: Record<AmbassadorContractType, string> = {
  freelancer: "프리랜서 (개인)",
  business_general: "사업자 (일반과세)",
  business_simplified: "사업자 (간이과세)",
};

// 주민등록번호 입력 자동 포맷 — 숫자만 남기고 6자리 뒤에 하이픈을 넣는다 (000000-0000000, 최대 13자리).
const formatRrnInput = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 13);
  return digits.length > 6
    ? `${digits.slice(0, 6)}-${digits.slice(6)}`
    : digits;
};

// 독립 웹 페이지 입력 표준 (PromotionBookPhoneNumberSubmit 선례)
const inputSx = {
  width: "100%",
  height: "54px",
  borderRadius: "12px",
  backgroundColor: theme.vars.palette.Surface.Forward,
  fontSize: "16px",
  borderColor: "background.level3",
  borderWidth: "1px",
  "--joy-focus-thickness": "0px",
  boxShadow: "0px 0px 0px transparent",
  "&:focus-within": {
    boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Content.Highlight}`,
  },
};

// 앰버서더 정산 정보·서류 제출 — 전자계약 체결 이후 수수료 정산·세무 처리에 필요한 정보를 받는다.
// 주민등록번호(프리랜서)·계좌번호는 계약서에 기재하지 않는 정산 정보라 여기서 별도 수집한다 (서버 암호화 저장).
// 계약 완료(어드민 승인) 전까지 재제출(덮어쓰기) 가능. 유형·실명 등 계약 정보 입력은 전자계약 페이지가 담당한다.
// 구 플로우(외부 체결 계약)로 제출·완료된 건은 기존 읽기 전용 화면을 유지한다.
export default function AmbassadorContract() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [contractType, setContractType] =
    useState<AmbassadorContractType>("freelancer");
  const [realName, setRealName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [contractEmail, setContractEmail] = useState("");
  const [contractPhoneNumber, setContractPhoneNumber] = useState("");
  const [activityName, setActivityName] = useState("");
  // 정산 정보 — 계좌는 서버가 원문을 돌려줘 프리필하고, 주민번호는 마스킹만 내려와 별도 표시한다.
  const [residentRegistrationNumber, setResidentRegistrationNumber] =
    useState("");
  // 이미 제출된 주민등록번호의 마스킹 표기 (예: 900101-1******) — 입력을 비워두면 이 값이 유지된다
  const [savedRrnMasked, setSavedRrnMasked] = useState<string | null>(null);
  const [bankName, setBankName] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [bankbookFile, setBankbookFile] = useState<string | null>(null);
  const [businessRegistrationFile, setBusinessRegistrationFile] = useState<
    string | null
  >(null);
  // 업로드한 파일의 원본 파일명 — 서버에는 저장하지 않으므로 기존 제출분은 null (표시 폴백: "저장된 파일")
  const [bankbookFileName, setBankbookFileName] = useState<string | null>(null);
  const [businessRegistrationFileName, setBusinessRegistrationFileName] =
    useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // 구 플로우 제출 여부 — 미서명 상태에서 제출돼 있으면(외부 체결 진행 중) 읽기 전용으로 보여준다
  const [submitted, setSubmitted] = useState(false);
  // 전자서명 체결 시각 — null 이면 미체결. 미체결·미제출이면 전자계약 페이지로 보낸다
  const [signedAt, setSignedAt] = useState<string | null>(null);
  // 이미 제출된 정산 서류가 있는지 (재제출 안내용) + 마지막 제출 시각
  const [documentsSubmitted, setDocumentsSubmitted] = useState(false);
  const [documentsSubmittedAt, setDocumentsSubmittedAt] = useState<
    string | null
  >(null);
  // 계약 완료 여부 + 체결된 계약서 사본 (어드민이 완료 처리 시 업로드) — 당사자 미리보기·다운로드용
  const [completed, setCompleted] = useState(false);
  const [contractFile, setContractFile] = useState<string | null>(null);
  const [contractFileName, setContractFileName] = useState<string | null>(null);
  // 제출 전 확인 팝업
  const [confirming, setConfirming] = useState(false);
  // 서류 제출 단계 동의 — 정산 정보(주민번호·계좌) 수집 직전에 받는다. 섹션 내용은 서버(계약형태 기준)가 내려준다.
  const [documentConsents, setDocumentConsents] = useState<
    AmbassadorContractAgreementSection[]
  >([]);
  const [agreedTax, setAgreedTax] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);

  const hasToken = !!getAmbassadorToken();

  // 기존 제출값 로드
  useEffect(() => {
    if (!hasToken) return;
    (async () => {
      try {
        const contractRes = await ambassadorApi.getContract();
        if (contractRes.code === 0 && contractRes.result) {
          const c = contractRes.result;
          setSubmitted(!!c.type);
          if (c.type) setContractType(c.type);
          setRealName(c.realName ?? "");
          setBusinessName(c.businessName ?? "");
          setContractEmail(c.email ?? "");
          setActivityName(c.name ?? "");
          setBankbookFile(c.bankbookFile ?? null);
          setBankbookFileName(c.bankbookFileName ?? null);
          setDocumentsSubmitted(!!c.bankbookFile);
          // 제출한 정산 정보 프리필 — 본인이 확인하고 그 자리에서 고칠 수 있게 한다
          setBankName(c.bankName ?? "");
          setBankAccount(c.bankAccount ?? "");
          setAccountHolderName(c.accountHolderName ?? "");
          setSavedRrnMasked(c.residentRegistrationNumberMasked ?? null);
          setDocumentsSubmittedAt(c.documentsSubmittedAt ?? null);
          setBusinessRegistrationFile(c.businessRegistrationFile ?? null);
          setBusinessRegistrationFileName(
            c.businessRegistrationFileName ?? null
          );
          setCompleted(!!c.completed);
          setContractFile(c.contractFile ?? null);
          setContractFileName(c.contractFileName ?? null);
          setSignedAt(c.signedAt ?? null);
          setDocumentConsents(c.documentConsents ?? []);
          const phone = c.phoneNumber;
          if (phone)
            setContractPhoneNumber(
              phone.startsWith("01") ? formatPhoneNumberInput(phone) : phone
            );
        }
      } catch (e) {
        // 401 은 인터셉터가 로그인으로 보낸다 — 그 외는 빈 폼으로 시작
      } finally {
        setLoading(false);
      }
    })();
  }, [hasToken]);

  if (!hasToken) {
    // 로그인 후 이 페이지로 복귀하도록 출발지를 state 로 전달한다 (로그인 페이지가 해석)
    return (
      <Navigate
        to={PATH.ambassadorLogin}
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // 미체결·미제출 계정은 전자계약 체결이 먼저다
  if (!loading && !signedAt && !submitted && !completed) {
    return <Navigate to={PATH.ambassadorContractSign} replace />;
  }

  const isBusiness = contractType !== "freelancer";
  const rrnDigits = residentRegistrationNumber.replace(/\D/g, "");
  const canSubmit =
    !submitting &&
    !loading &&
    !completed &&
    !!bankbookFile &&
    // 프리랜서 주민번호 — 이미 제출본이 있으면 비워둬도 된다(서버가 기존 값 유지), 입력했다면 13자리여야 한다
    (isBusiness
      ? !!businessRegistrationFile
      : savedRrnMasked
        ? rrnDigits.length === 0 || rrnDigits.length === 13
        : rrnDigits.length === 13) &&
    bankName.trim().length > 0 &&
    bankAccount.replace(/\D/g, "").length >= 8 &&
    accountHolderName.trim().length > 0 &&
    agreedTax &&
    agreedPrivacy;

  const handleSubmit = async () => {
    if (!canSubmit || !bankbookFile) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await ambassadorApi.submitContractDocuments({
        // 비워두면 null — 서버가 기존 주민등록번호를 유지한다 (마스킹 값을 되돌려 보내지 않는다)
        residentRegistrationNumber: isBusiness || !rrnDigits ? null : rrnDigits,
        bankName: bankName.trim(),
        bankAccount: bankAccount.replace(/\D/g, ""),
        accountHolderName: accountHolderName.trim(),
        bankbookFile,
        bankbookFileName,
        businessRegistrationFile: isBusiness ? businessRegistrationFile : null,
        businessRegistrationFileName: isBusiness
          ? businessRegistrationFileName
          : null,
        agreedTax,
        agreedPrivacy,
      });
      if (res.code === 0) {
        setDone(true);
        return;
      }
      setConfirming(false);
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setConfirming(false);
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const contractTypeLabel = CONTRACT_TYPE_LABELS[contractType] ?? contractType;
  // 읽기 전용 화면 — 계약 완료 건과, 미서명 상태로 구 플로우 제출을 마친 건(외부 체결 진행 중)
  const readOnly = completed || (!signedAt && submitted);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.vars.palette.Background.Level1,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: "480px",
          px: "20px",
          py: "40px",
        }}
      >
        {done ? (
          <Stack spacing={4} alignItems="center" sx={{ pt: "48px" }}>
            <img
              src={distancingLogo}
              alt="distancing logo"
              style={{ width: "138px", height: "auto" }}
            />
            <Stack spacing={"12px"} alignItems="center">
              <CustomTypography
                variant="H3"
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {"정산 정보가 제출됐어요"}
              </CustomTypography>
              <CustomTypography
                variant="Body"
                sx={{
                  textAlign: "center",
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {"담당자가 확인한 뒤 계약이 완료돼요."}
              </CustomTypography>
            </Stack>
            <Button
              variant="Action"
              size="lg"
              onClick={() => navigate(PATH.ambassador)}
              sx={{
                width: "100%",
                maxWidth: "345px",
                height: "54px",
                fontWeight: 700,
                borderRadius: "12px",
                fontSize: "16px",
              }}
            >
              {"홈으로 돌아가기"}
            </Button>
          </Stack>
        ) : (
          <>
            <Box sx={{ display: "flex" }}>
              <LinkV2
                variant="Caption"
                text="← 홈으로"
                onClick={() => navigate(PATH.ambassador)}
              />
            </Box>
            <Stack spacing={"8px"} sx={{ mt: 2 }}>
              <CustomTypography
                variant="H3"
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {readOnly ? "계약 정보" : "정산 정보·서류 제출"}
              </CustomTypography>
              <CustomTypography
                variant="Caption"
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                }}
              >
                {readOnly
                  ? "계약서 발송과 정산에 필요한 정보예요."
                  : "수수료 정산과 세무 처리에 필요한 정보예요. 계약서에는 기재되지 않아요."}
              </CustomTypography>
              {/* 체결본 바로가기 — 계약 정보 메뉴로 들어온 사용자가 제출 전에도 체결된 계약서를 확인할 수 있게 */}
              {!readOnly && signedAt && (
                <LinkV2
                  variant="Caption"
                  text="체결된 전자계약서 보기"
                  customSx={{
                    textDecoration: "underline",
                    width: "fit-content",
                  }}
                  onClick={() => navigate(PATH.ambassadorContractSign)}
                />
              )}
            </Stack>

            {loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center", py: "80px" }}
              >
                <CircularProgress color="neutral" />
              </Box>
            ) : readOnly ? (
              // 계약 완료(또는 외부 체결 진행 중) — 읽기 전용으로 보여준다
              <Stack spacing={"20px"} sx={{ mt: 4 }}>
                <InfoRow label="계약형태" value={contractTypeLabel} />
                {isBusiness ? (
                  <InfoRow label="사업자명" value={businessName || "-"} />
                ) : (
                  <InfoRow label="실명" value={realName || "-"} />
                )}
                <InfoRow label="이메일" value={contractEmail || "-"} />
                <InfoRow label="전화번호" value={contractPhoneNumber || "-"} />
                <InfoRow label="활동명" value={activityName || "-"} />
                {bankbookFile && (
                  <FilePreviewRow label="통장사본" file={bankbookFile} />
                )}
                {isBusiness && businessRegistrationFile && (
                  <FilePreviewRow
                    label="사업자등록증"
                    file={businessRegistrationFile}
                  />
                )}
                {/* 전자계약 체결 건 — 체결본 열람은 전자계약 페이지가 담당 */}
                {signedAt && (
                  <Stack spacing={"2px"}>
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {"전자계약서"}
                    </CustomTypography>
                    <LinkV2
                      variant="Caption"
                      text="체결본 보기"
                      customSx={{
                        textDecoration: "underline",
                        width: "fit-content",
                      }}
                      onClick={() => navigate(PATH.ambassadorContractSign)}
                    />
                  </Stack>
                )}
                {/* 체결된 계약서 — 외부 체결 건은 어드민이 업로드한 사본을 확인·보관할 수 있다 */}
                {completed && contractFile && (
                  <Stack spacing={"2px"}>
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {"체결된 계약서"}
                    </CustomTypography>
                    <Stack direction="row" spacing={"12px"}>
                      <LinkV2
                        variant="Caption"
                        text="미리보기"
                        customSx={{
                          textDecoration: "underline",
                          width: "fit-content",
                        }}
                        onClick={() => openDataUrlPreview(contractFile)}
                      />
                      <LinkV2
                        variant="Caption"
                        text="다운로드"
                        customSx={{
                          textDecoration: "underline",
                          width: "fit-content",
                        }}
                        onClick={() =>
                          downloadDataUrl(
                            contractFile,
                            "디스턴싱-앰버서더-계약서",
                            contractFileName
                          )
                        }
                      />
                    </Stack>
                  </Stack>
                )}
                <CustomTypography
                  variant="Footnote"
                  sx={{
                    fontWeight: 500,
                    color: theme.vars.palette.Content.Weak,
                  }}
                >
                  {completed
                    ? "정보 변경이 필요하면 담당자에게 문의해 주세요."
                    : "담당자가 계약을 진행하고 있어요. 변경이 필요하면 담당자에게 문의해 주세요."}
                </CustomTypography>
              </Stack>
            ) : (
              // 전자서명 완료 · 계약 완료 전 — 정산 정보·서류 제출 폼 (재제출 허용)
              <Stack spacing={4} sx={{ mt: 4 }}>
                {/* 제출 완료 안내 — 아래 폼에 제출한 값이 채워져 있고, 고쳐서 다시 제출할 수 있다 */}
                {documentsSubmitted && (
                  <Box
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: theme.vars.palette.Surface.SoftHighlight,
                      px: "16px",
                      py: "12px",
                    }}
                  >
                    <CustomTypography
                      variant="Caption"
                      sx={{
                        fontWeight: 700,
                        color: theme.vars.palette.Content.Highlight,
                      }}
                    >
                      {documentsSubmittedAt
                        ? `제출 완료 · ${formatSubmittedAt(documentsSubmittedAt)}`
                        : "제출 완료"}
                    </CustomTypography>
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        mt: "2px",
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Highlight,
                      }}
                    >
                      {
                        "담당자 확인 전까지는 내용을 고쳐 다시 제출할 수 있어요."
                      }
                    </CustomTypography>
                  </Box>
                )}

                {!isBusiness && (
                  <Stack spacing={"6px"}>
                    <FieldLabel text="주민등록번호" />
                    {/* 제출본은 마스킹으로만 보여준다 — 바꿀 때만 아래에 새로 입력하면 되고, 비워두면 그대로 유지된다 */}
                    {savedRrnMasked && (
                      <CustomTypography
                        variant="Body"
                        sx={{
                          fontWeight: 500,
                          color: theme.vars.palette.Content.Strong,
                        }}
                      >
                        {`제출됨 · ${savedRrnMasked}`}
                      </CustomTypography>
                    )}
                    <Input
                      sx={inputSx}
                      slotProps={{ input: { inputMode: "numeric" } }}
                      placeholder={
                        savedRrnMasked
                          ? "변경할 때만 새로 입력해 주세요"
                          : "000000-0000000"
                      }
                      value={residentRegistrationNumber}
                      onChange={(e) =>
                        setResidentRegistrationNumber(
                          formatRrnInput(e.target.value)
                        )
                      }
                    />
                    <CustomTypography
                      variant="Footnote"
                      sx={{
                        fontWeight: 500,
                        color: theme.vars.palette.Content.Weak,
                      }}
                    >
                      {
                        "소득세법에 따른 원천징수·지급명세서 제출에 사용돼요. 암호화해 안전하게 보관해요."
                      }
                    </CustomTypography>
                  </Stack>
                )}

                <Stack spacing={"6px"}>
                  <FieldLabel text="정산 계좌" />
                  <Input
                    sx={inputSx}
                    placeholder="은행명"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                  />
                  <Input
                    sx={inputSx}
                    slotProps={{ input: { inputMode: "numeric" } }}
                    placeholder="계좌번호"
                    value={bankAccount}
                    onChange={(e) =>
                      setBankAccount(e.target.value.replace(/[^\d-]/g, ""))
                    }
                  />
                  <Input
                    sx={inputSx}
                    placeholder="예금주"
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                  />
                  <CustomTypography
                    variant="Footnote"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Weak,
                    }}
                  >
                    {isBusiness
                      ? "사업자 명의 계좌를 입력해 주세요."
                      : "계약서상 실명과 동일한 명의의 계좌를 입력해 주세요."}
                  </CustomTypography>
                </Stack>

                <FileField
                  label="통장사본"
                  value={bankbookFile}
                  fileName={bankbookFileName}
                  onChange={(dataUrl, name) => {
                    setBankbookFile(dataUrl);
                    setBankbookFileName(name);
                  }}
                  onError={setError}
                />

                {isBusiness && (
                  <FileField
                    label="사업자등록증"
                    value={businessRegistrationFile}
                    fileName={businessRegistrationFileName}
                    onChange={(dataUrl, name) => {
                      setBusinessRegistrationFile(dataUrl);
                      setBusinessRegistrationFileName(name);
                    }}
                    onError={setError}
                  />
                )}

                {/* 서류 제출 단계 동의 — 세무 처리 확인·개인정보 수집. 정산 정보 수집 직전이 동의 시점이다 */}
                {documentConsents.map((section) => (
                  <ConsentCard
                    key={section.key}
                    section={section}
                    checked={
                      section.key === "tax"
                        ? agreedTax
                        : section.key === "privacy"
                          ? agreedPrivacy
                          : false
                    }
                    onChange={(checked) => {
                      if (section.key === "tax") setAgreedTax(checked);
                      if (section.key === "privacy") setAgreedPrivacy(checked);
                    }}
                    linkText={
                      section.key === "privacy" ? "약관 보기" : undefined
                    }
                    onLink={
                      section.key === "privacy"
                        ? () => window.open(PATH.ambassadorTerms)
                        : undefined
                    }
                  />
                ))}

                {error && (
                  <CustomTypography
                    variant="Caption"
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.HighlightWithAlert,
                    }}
                  >
                    {error}
                  </CustomTypography>
                )}

                <Button
                  variant="Action"
                  size="lg"
                  disabled={!canSubmit}
                  onClick={() => setConfirming(true)}
                  sx={{
                    width: "100%",
                    height: "54px",
                    fontWeight: 700,
                    borderRadius: "12px",
                    fontSize: "16px",
                  }}
                >
                  {"제출하기"}
                </Button>
              </Stack>
            )}

            {/* 제출 전 확인 팝업 */}
            {confirming && (
              <CustomModal
                open
                onClose={() => setConfirming(false)}
                title="제출 전에 확인해 주세요"
                subtitle="담당자가 확인한 뒤 계약이 완료돼요."
                actions={[
                  {
                    label: "제출하기",
                    onClick: handleSubmit,
                    variant: "Solid",
                    buttonProps: { loading: submitting },
                  },
                  {
                    label: "취소",
                    onClick: () => setConfirming(false),
                    variant: "Outlined",
                  },
                ]}
              >
                <Stack spacing={"12px"}>
                  {!isBusiness && (
                    <InfoRow
                      label="주민등록번호"
                      value={
                        rrnDigits.length === 13
                          ? `${rrnDigits.slice(0, 6)}-${rrnDigits.slice(6, 7)}******`
                          : savedRrnMasked
                            ? `${savedRrnMasked} (변경 없음)`
                            : "-"
                      }
                    />
                  )}
                  <InfoRow
                    label="정산 계좌"
                    value={
                      `${bankName.trim()} ${bankAccount} (${accountHolderName.trim()})` ||
                      "-"
                    }
                  />
                  <InfoRow
                    label="통장사본"
                    value={bankbookFileName ?? (bankbookFile ? "첨부됨" : "-")}
                  />
                  {isBusiness && (
                    <InfoRow
                      label="사업자등록증"
                      value={
                        businessRegistrationFileName ??
                        (businessRegistrationFile ? "첨부됨" : "-")
                      }
                    />
                  )}
                </Stack>
              </CustomModal>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// 서류 제출 단계 동의 카드 — 섹션 요점을 보여주고 동의 체크를 받는다 (내용은 서버가 계약형태 기준으로 내려준다).
function ConsentCard({
  section,
  checked,
  onChange,
  linkText,
  onLink,
}: {
  section: AmbassadorContractAgreementSection;
  checked: boolean;
  onChange: (checked: boolean) => void;
  linkText?: string;
  onLink?: () => void;
}) {
  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        backgroundColor: theme.vars.palette.Surface.Forward,
        px: "16px",
        py: "14px",
      }}
    >
      <CustomTypography
        variant="Caption"
        sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
      >
        {section.title}
      </CustomTypography>
      <Stack spacing={"6px"} sx={{ mt: "8px", mb: "12px" }}>
        {section.items.map((item) => (
          <Stack
            key={item}
            direction="row"
            spacing={"6px"}
            alignItems="flex-start"
          >
            <CustomTypography
              variant="Footnote"
              sx={{
                flexShrink: 0,
                fontWeight: 500,
                color: theme.vars.palette.Content.Weak,
              }}
            >
              {"•"}
            </CustomTypography>
            <CustomTypography
              variant="Footnote"
              sx={{
                fontWeight: 500,
                color: theme.vars.palette.Content.Medium,
                wordBreak: "keep-all",
                overflowWrap: "anywhere",
              }}
            >
              {item}
            </CustomTypography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
        <Checkbox
          size="sm"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          sx={{ mt: "2px" }}
        />
        <CustomTypography
          variant="Caption"
          sx={{ fontWeight: 500, color: theme.vars.palette.Content.Medium }}
        >
          <Box
            component="span"
            sx={{ cursor: "pointer" }}
            onClick={() => onChange(!checked)}
          >
            {"위 내용을 확인했고 이에 동의합니다."}
          </Box>
          {linkText && onLink && (
            <Box
              component="span"
              sx={{
                ml: "6px",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={onLink}
            >
              {linkText}
            </Box>
          )}
        </CustomTypography>
      </Box>
    </Box>
  );
}

// 제출 시각(ISO) → "2026. 8. 14. 10:48" 표기. 파싱 실패 시 원문 그대로.
function formatSubmittedAt(submittedAt: string): string {
  const date = new Date(submittedAt);
  if (Number.isNaN(date.getTime())) return submittedAt;
  return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}. ${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
}

// 읽기 전용 표시·제출 확인 팝업 공용 라벨/값 행
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={"2px"}>
      <CustomTypography
        variant="Footnote"
        sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="Body"
        sx={{
          fontWeight: 500,
          color: theme.vars.palette.Content.Strong,
          wordBreak: "break-all",
        }}
      >
        {value}
      </CustomTypography>
    </Stack>
  );
}

// 읽기 전용 화면의 저장된 파일 미리보기 행
function FilePreviewRow({ label, file }: { label: string; file: string }) {
  return (
    <Stack spacing={"2px"}>
      <CustomTypography
        variant="Footnote"
        sx={{ fontWeight: 500, color: theme.vars.palette.Content.Weak }}
      >
        {label}
      </CustomTypography>
      <LinkV2
        variant="Caption"
        text="미리보기"
        customSx={{ textDecoration: "underline", width: "fit-content" }}
        onClick={() => openDataUrlPreview(file)}
      />
    </Stack>
  );
}

function FieldLabel({ text }: { text: string }) {
  return (
    <CustomTypography
      variant="Caption"
      sx={{ fontWeight: 700, color: theme.vars.palette.Content.Strong }}
    >
      {text}
    </CustomTypography>
  );
}

// data URL 을 파일로 다운로드 — 업로드 당시의 원본 파일명(fileName)이 있으면 그대로 쓰고,
// 없으면(컬럼 추가 이전에 올라간 건) MIME 으로 확장자를 정해 규칙 이름을 만든다.
function downloadDataUrl(
  dataUrl: string,
  baseName: string,
  fileName?: string | null
) {
  const mime = dataUrl.slice(5, dataUrl.indexOf(";"));
  const ext = mime === "application/pdf" ? "pdf" : mime.split("/")[1] || "bin";
  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = fileName || `${baseName}.${ext}`;
  anchor.click();
}

// data URL 을 새 탭에서 미리보기 — 브라우저가 data: URL 의 최상위 탐색을 막으므로 Blob URL 로 변환해 연다.
function openDataUrlPreview(dataUrl: string) {
  const [meta, base64] = dataUrl.split(",");
  const mime =
    meta.match(/^data:(.*?);base64$/)?.[1] ?? "application/octet-stream";
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: mime }));
  window.open(url);
  // 즉시 revoke 하면 새 탭이 로드하기 전에 무효화된다 — 여유를 두고 해제
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// 파일 업로드 필드 — 이미지/PDF 를 base64 data URL 로 읽어 상태에 보관. 클릭 선택 외에 드래그앤드롭도 지원.
// 파일명 표시(기존 제출분은 원본명이 없어 "저장된 파일" 폴백), 이미지는 섬네일, 미리보기는 새 탭으로 연다.
function FileField({
  label,
  value,
  fileName,
  onChange,
  onError,
}: {
  label: string;
  value: string | null;
  fileName: string | null;
  onChange: (dataUrl: string | null, fileName: string | null) => void;
  onError: (message: string | null) => void;
}) {
  const [dragging, setDragging] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    // 드롭된 파일은 input accept 필터를 안 거친다 — 클릭·드롭 공통으로 형식을 검사한다
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      onError("이미지 또는 PDF 파일만 올릴 수 있어요.");
      return;
    }
    if (file.size > CONTRACT_FILE_MAX_BYTES) {
      onError("파일 크기는 5MB 이하로 올려 주세요.");
      return;
    }
    try {
      onChange(await readFileAsDataUrl(file), file.name);
      onError(null);
    } catch (e) {
      onError(FALLBACK_ERROR_MESSAGE);
    }
  };

  return (
    <Stack
      spacing={"6px"}
      // 필드 전체가 드롭 영역 — 드래그 중에는 점선 외곽선으로 표시한다 (outline 이라 레이아웃 이동 없음)
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        // 자식 요소로 이동할 때 발생하는 leave 는 무시한다 (깜빡임 방지)
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files?.[0]);
      }}
      sx={{
        borderRadius: "12px",
        outline: dragging
          ? `2px dashed ${theme.vars.palette.Content.Highlight}`
          : "none",
        outlineOffset: "6px",
      }}
    >
      <FieldLabel text={label} />
      {value && value.startsWith("data:image") && (
        <Box
          component="img"
          src={value}
          alt={label}
          onClick={() => openDataUrlPreview(value)}
          sx={{
            maxHeight: "160px",
            width: "fit-content",
            maxWidth: "100%",
            borderRadius: "12px",
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            objectFit: "contain",
            backgroundColor: theme.vars.palette.Surface.Forward,
            cursor: "zoom-in",
          }}
        />
      )}
      {value && (
        <Stack direction="row" alignItems="center" spacing={"8px"}>
          {/* 파일명 자체가 미리보기 링크 */}
          <CustomTypography
            variant="Caption"
            onClick={() => openDataUrlPreview(value)}
            sx={{
              fontWeight: 500,
              color: theme.vars.palette.Content.Medium,
              wordBreak: "break-all",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {fileName ?? "저장된 파일"}
          </CustomTypography>
          {/* 삭제 — X 아이콘 */}
          <CloseRoundedIcon
            titleAccess="파일 삭제"
            onClick={() => onChange(null, null)}
            sx={{
              flexShrink: 0,
              fontSize: "16px",
              color: theme.vars.palette.Content.Weak,
              cursor: "pointer",
            }}
          />
        </Stack>
      )}
      <Button
        component="label"
        variant="Outlined"
        size="sm"
        sx={{ width: "fit-content", fontWeight: 700 }}
      >
        {value ? "다시 올리기" : "파일 올리기"}
        <input
          hidden
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </Button>
      <CustomTypography
        variant="Footnote"
        sx={{ fontWeight: 500, color: theme.vars.palette.Content.SuperWeak }}
      >
        {"이미지 또는 PDF, 5MB 이하"}
      </CustomTypography>
    </Stack>
  );
}
